import PropTypes from 'prop-types';
import Switch from 'react-router-dom/Switch';
import Route from 'react-router-dom/Route';
import { Redirect } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useStripes } from '@folio/stripes/core';
import { MessageBanner } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import Settings from './settings';
import PrefferedReportsRoute from './routes/PrefferedReportsRoute';
import ScheduledReportsRoute from './routes/ScheduledReportsRoute';
import DashboardsProvider from './Context/dashboards/DashboardsProvider';
import AppConfigListProvider from './Context/appConfigList/AppConfigListProvider';
import WidgetFormRoute from './routes/WidgetFormRoute';
import WidgetBuilderRoute from './routes/reportBuilders/WidgetBuilderRoute';
import DashboardsRoute from './routes/DashboardsRoute';
import ReportsProvider from './Context/reports/ReportsProvider';
import QueryBuilderRoute from './routes/reportBuilders/QueryBuilderRoute';
import CatalogingReportsRoute from './routes/reportBuilders/CatalogingReportsRoute';
import ProductivityReportsRoute from './routes/reportBuilders/ProductivityReportsRoute';
import ReportsListRoute from './routes/ReportsListRoute';
import EditQueryRoute from './routes/EditReportQueryRoute';
import EditMetadataRoute from './routes/EditReportMetadataRoute';
import ReportBuilderSelectionRoute from './routes/reportBuilders/ReportBuilderSelectionRoute';
import CirculationReportsRoute from './routes/reportBuilders/CirculationReportsRoute';
import RoutesTabs from './routes/RoutesTabs';
import css from './routes/style.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Smart redirect — navigates to first tab the user has permission to view
const ORDERED_TABS = [
  { perm: 'ui-reports.dashboards.view',        path: 'dashboards' },
  { perm: 'ui-reports.reports.view',           path: 'reports-list' },
  { perm: 'ui-reports.reports.execute',        path: 'report-builder' },
  { perm: 'ui-reports.scheduled-reports.view', path: 'scheduled-reports' },
  { perm: 'ui-reports.favorite-reports.view',  path: 'preffered-reports' },
];

function SmartRedirect({ basePath }) {
  const stripes = useStripes();
  const firstAllowed = ORDERED_TABS.find(tab => stripes.hasPerm(tab.perm));
  if (firstAllowed) return <Redirect to={`${basePath}/${firstAllowed.path}`} />;
  return (
    <MessageBanner type="warning">
      <FormattedMessage
        id="ui-reports.noPermissions"
        defaultMessage="You do not have permission to access any section of this module. Please contact your system administrator."
      />
    </MessageBanner>
  );
}

SmartRedirect.propTypes = { basePath: PropTypes.string.isRequired };

function Reports(props) {
  const {
    actAs,
    match: { path },
  } = props;
  if (actAs === 'settings') {
    return <Settings {...props} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AppConfigListProvider>
          <DashboardsProvider>
            <ReportsProvider>
              <div className={css.appContainer}>
                <RoutesTabs />
                <div className={css.mainContent}>
                  <Switch>
                    <Route exact path={path} render={() => <SmartRedirect basePath={path} />} />
                    <Route
                      path={`${path}/dashboards/:id`}
                      exact
                      component={DashboardsRoute}
                    />
                    <Route
                      path={`${path}/dashboards`}
                      exact
                      component={DashboardsRoute}
                    />

                    <Route
                      path={`${path}/dashboards/:id/widgets/create/builder/:builderType`}
                      exact
                      component={WidgetBuilderRoute}
                    />
                    <Route
                      path={`${path}/dashboards/:id/widgets/create`}
                      exact
                      component={WidgetFormRoute}
                    />
                    <Route
                      path={`${path}/dashboards/:id/widgets/edit/:widgetId`}
                      component={WidgetFormRoute}
                    />

                    <Route
                      path={`${path}/reports-list`}
                      exact
                      component={ReportsListRoute}
                    />
                    <Route
                      path={`${path}/reports-list/:reportId/view`}
                      exact
                      component={ReportsListRoute}
                    />

                    <Route
                      path={`${path}/reports-list/:reportId/edit-metadata`}
                      exact
                      component={EditMetadataRoute}
                    />

                    <Route
                      path={`${path}/reports-list/:reportId/edit-query`}
                      exact
                      component={EditQueryRoute}
                    />

                    <Route
                      path={`${path}/preffered-reports`}
                      exact
                      component={PrefferedReportsRoute}
                    />
                    <Route
                      path={`${path}/scheduled-reports/:scheduleId/view`}
                      component={ScheduledReportsRoute}
                    />
                    <Route
                      path={`${path}/scheduled-reports`}
                      exact
                      component={ScheduledReportsRoute}
                    />
                    <Route
                      path={`${path}/query-builder`}
                      exact
                      component={QueryBuilderRoute}
                    />
                    <Route
                      path={`${path}/cataloging-reports`}
                      exact
                      component={CatalogingReportsRoute}
                    />
                    <Route
                      path={`${path}/circulation-reports`}
                      exact
                      component={CirculationReportsRoute}
                    />
                    <Route
                      path={`${path}/productivity-reports`}
                      exact
                      component={ProductivityReportsRoute}
                    />
                    <Route
                      path={`${path}/select-builder`}
                      exact
                      component={ReportBuilderSelectionRoute}
                    />
                    <Route
                      path={`${path}/report-builder`}
                      exact
                      component={ReportBuilderSelectionRoute}
                    />
                  </Switch>
                </div>
              </div>
            </ReportsProvider>
          </DashboardsProvider>
        </AppConfigListProvider>
    </QueryClientProvider>
  );
}

Reports.propTypes = {
  match: PropTypes.object.isRequired,
  showSettings: PropTypes.bool,
  stripes: PropTypes.shape({
    connect: PropTypes.func,
    okapi: PropTypes.shape({
      token: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.object,
  actAs: PropTypes.string.isRequired,
};

export default Reports;
